import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { createDynamicCSS } from "../../utils/CreateDynamicCSS";

export default function AltAccountWarning() {
  const { t } = useTranslation();
  const [clickOpen, setClickOpen] = useState(false);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [posClass, setPosClass] = useState("");
  const iconRef = useRef(null);
  const rootRef = useRef(null);

  const message = t("altAccountWarningMessage");
  const visible = clickOpen || hoverOpen;

  const updatePosition = useCallback(() => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) {
      setPosClass("");
      return;
    }

    const gap = 8;
    const topOffset = 5;
    const left = rect.left - gap;
    const top = rect.top + topOffset;
    const cls = `alt-account-tt-${Math.round(top)}-${Math.round(left)}`;
    createDynamicCSS(cls, {
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 100000,
    });
    setPosClass(cls);
  }, []);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setClickOpen((open) => !open);
  };

  const handleMouseEnter = () => {
    setHoverOpen(true);
  };

  const handleMouseLeave = () => {
    setHoverOpen(false);
  };

  const handleFocus = () => {
    setHoverOpen(true);
  };

  const handleBlur = (e) => {
    if (rootRef.current?.contains(e.relatedTarget)) return;
    setHoverOpen(false);
    setClickOpen(false);
  };

  useLayoutEffect(() => {
    if (!visible) {
      setPosClass("");
      return undefined;
    }

    updatePosition();
    const raf = window.requestAnimationFrame(updatePosition);
    const onReflow = () => updatePosition();
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [visible, updatePosition, message]);

  useEffect(() => {
    if (!clickOpen) return undefined;

    const handleDocumentClick = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      setClickOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setClickOpen(false);
    };

    const timer = window.setTimeout(() => {
      document.addEventListener("click", handleDocumentClick);
    }, 0);

    document.addEventListener("keydown", handleEscape);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("click", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [clickOpen]);

  const tooltip = visible && posClass ? (
    <div
      className={[
        "alt-account-warning__tooltip",
        "global",
        posClass,
      ].join(" ")}
      role="tooltip"
    >
      <span className="alt-account-warning__tooltip-accent" aria-hidden="true" />
      <span className="alt-account-warning__tooltip-icon" aria-hidden="true">⚠</span>
      <p className="alt-account-warning__tooltip-text">{message}</p>
    </div>
  ) : null;

  return (
    <span
      ref={rootRef}
      className="alt-account-warning alt-account-warning--header"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        ref={iconRef}
        type="button"
        className="alt-account-warning__icon"
        aria-label={message}
        aria-expanded={visible}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        ⚠
      </button>

      {tooltip && typeof document !== "undefined"
        ? createPortal(tooltip, document.body)
        : null}
    </span>
  );
}
